import { isPlatformBrowser, Location } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { forkJoin, Subscription } from 'rxjs';
import { SubSink } from 'subsink';
import { RouterPathConfig } from '../core/constants/router-path.config';
import { TuwaiqConfig } from '../core/constants/tuwaiq.config';
import { USER_ROLE } from '../core/constants/user-role.enum';
import { VIEW_TYPE } from '../core/constants/view-type.enum';
import { RequestCountResponse } from '../core/models/requests-count.model';
import { ViewDetails } from '../core/models/view-details.model';
import { ApiRequestService } from '../core/services/api-request.service';
import { LoggedInUserService } from '../core/services/logged-in-user.service';
import { RequestViewService } from '../core/services/request-view.service';
import { RoutingHandlerService } from '../core/services/routing-handler.service';
import { SidebarMenuService } from '../core/services/sidebar-menu.service';
import { DelegationService } from '../delegation/services/delegation.service';
import { ScreenResizeService } from '../shared/services/screen-resize.service';

@Component({
    selector: 'app-tuwaiq-layout',
    templateUrl: './tuwaiq-layout.component.html',
    styleUrls: ['./tuwaiq-layout.component.css', './tuwaiq-layout.responsive.component.scss']
})
export class TuwaiqLayoutComponent implements OnInit, OnDestroy {
    userPath: string;
    userRole: USER_ROLE;
    sideMenuList: ViewDetails[] = [];
    showLoader = false;
    showLoaderOverlay = false;
    subs = new SubSink();
    showBodyContent = false;
    currentUrl;
    previousUrl;
    @ViewChild('scrollEl', { read: ElementRef }) tableInput: ElementRef;
    screenSize: string;
    resizeSubscription$: Subscription;
    isDelegationActive = false;
    isDelegationRoute = false; // Added to check if we're on a delegation route

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private loggedInUserService: LoggedInUserService,
        private routingHandlerService: RoutingHandlerService,
        private location: Location,
        private sidebarMenuService: SidebarMenuService,
        private apiRequestService: ApiRequestService,
        private requestsService: RequestViewService,
        private cookies: CookieService,
        @Inject(PLATFORM_ID) private platformId: Object,
        private screenResizeService: ScreenResizeService,
        private delegationService: DelegationService
    ) {}

    ngOnInit(): void {
        this.showLoader = true;
        this.isDelegationActive = this.delegationService.isDelegationActive();
        this.handleOnRouterChange();
        this.getUserPathUrl();
        this.getSideMenuList();
        this.getCountList();
        this.subscribeToUpdateCount();
        this.handleLoaderOverlay();
        this.subScrollToTp();

        this.resizeSubscription$ = this.screenResizeService.onResize$.subscribe((screenSize: string) => {
            this.screenSize = screenSize;
        });

        // Check if current route is delegation route
        this.checkIfDelegationRoute();
    }

    // Add method to check if we're on a delegation route
    checkIfDelegationRoute(): void {
        const currentPath = this.location.path();
        this.isDelegationRoute = currentPath.includes('/delegation');
    }

    handleOnRouterChange() {
        this.currentUrl = this.router.url;
        this.subs.sink = this.router.events.subscribe(event => {
            this.showBodyContent = false;
            if (event instanceof NavigationEnd) {
                this.routingHandlerService.onRouterChangeSetActiveHeaderMenu.next(true);

                // Check if this is a delegation route
                this.checkIfDelegationRoute();

                // Only get side menu list if not on delegation route
                if (!this.isDelegationRoute) {
                    this.getSideMenuList();
                }

                this.previousUrl = this.currentUrl;
                this.currentUrl = event.url;
                this.getCountList();
                setTimeout(() => {
                    this.showBodyContent = true;
                }, 50);

                // Only set router and activate menu item if not on delegation route
                if (!this.isDelegationRoute) {
                    this.setRouterAndActivateMenuItemBasedOnUrlPath();
                }
            }
        });
    }

    getCountList() {
        const activeHeaderMenu = this.sidebarMenuService.getActiveHeaderMenuItem();
        this.showLoader = true;

        // Skip if on delegation route
        if (this.isDelegationRoute) {
            this.showLoader = false;
            this.showBodyContent = true;
            return;
        }

        if (activeHeaderMenu?.id === 5 || activeHeaderMenu?.id === 1 || activeHeaderMenu?.id === 13) {
            if (this.loggedInUserService.isDisplayCallRecord()) {
                forkJoin([this.apiRequestService.getRequestsCount(), this.apiRequestService.getCallRecordCount()]).subscribe((response: any) => {
                    const allCounts = { ...response[0].data, ...response[1].data };
                    this.updateCount(allCounts);
                    this.setRouterAndActivateMenuItemBasedOnUrlPath();
                    this.showLoader = false;
                    this.showBodyContent = true;
                });
            } else {
                this.apiRequestService.getRequestsCount().subscribe((response: any) => {
                    this.updateCount(response.data);
                    this.setRouterAndActivateMenuItemBasedOnUrlPath();
                    this.showLoader = false;
                    this.showBodyContent = true;
                });
            }
        } else {
            this.setRouterAndActivateMenuItemBasedOnUrlPath();
            this.showLoader = false;
            this.showBodyContent = true;
        }
    }

    getUserPathUrl() {
        this.userRole = this.loggedInUserService.getUserRole();
        this.userPath = this.routingHandlerService.getUserPathFromRole(this.userRole);
    }

    getSideMenuList() {
        // Don't load side menu if on delegation route
        if (this.isDelegationRoute) {
            this.sideMenuList = [];
            return;
        }

        let activeHeaderMenu = this.sidebarMenuService.getActiveHeaderMenuItem();
        activeHeaderMenu = activeHeaderMenu === undefined ? { id: 5, name: '', path: '' } : activeHeaderMenu;

        this.sideMenuList = [];

        if (
            activeHeaderMenu?.id === 1 ||
            activeHeaderMenu?.id === 2 ||
            activeHeaderMenu?.id === 5 ||
            activeHeaderMenu?.id === 7 ||
            activeHeaderMenu?.id === 6 ||
            activeHeaderMenu?.id === 0 ||
            activeHeaderMenu?.id === 13
        ) {
            //home
            //NOTE: count will be zero at this point
            this.sideMenuList = this.sidebarMenuService.getSideMenuBasedOnUserRole(this.userRole) || [];
        } else if (activeHeaderMenu?.id === 4) {
            this.sideMenuList = this.sidebarMenuService.getPanelSideMenu() || [];
        } else if (activeHeaderMenu?.id === 10) {
            this.sideMenuList = this.sidebarMenuService.getFileManagementMenu() || [];
        } else if (activeHeaderMenu?.id === 11) {
            this.sideMenuList = this.sidebarMenuService.getPeriodicReviewMenu() || [];
        }
    }

    setRouterAndActivateMenuItemBasedOnUrlPath() {
        if (!this.sideMenuList || this.sideMenuList.length === 0) {
            return; // Don't proceed if no menu items
        }

        const urlWithOutParams = this.location.path().split('?')[0];
        const urlAfterUserPath = urlWithOutParams.split(this.userPath).pop();
        const segmentCount = urlAfterUserPath.split('/').length - 1;
        const getUserPathFromURL = urlWithOutParams.trim().split('/')[1];

        if (this.userPath !== getUserPathFromURL) {
            if (getUserPathFromURL === RouterPathConfig.DATA_MANAGEMENT) {
                this.handleDataManagementPanel(urlAfterUserPath);
            } else if (getUserPathFromURL === RouterPathConfig.FILE_MANAGEMENT) {
                this.handleDataManagementPanel(urlAfterUserPath);
            } else if (getUserPathFromURL === RouterPathConfig.PERIODIC_REVIEW) {
                this.handleDataManagementPanel(urlAfterUserPath);
            } else {
                this.setRouteAndFirstMenuItemAsDefault(this.sideMenuList);
            }
            return;
        }
        if (segmentCount === 0) {
            this.setRouteAndFirstMenuItemAsDefault(this.sideMenuList);
        } else if (segmentCount === 1) {
            // URL is userRole/something
            const secondaryPath = urlAfterUserPath.replace('/', '');
            const pathDetails = this.routingHandlerService.validatePath(this.sideMenuList, secondaryPath);
            if (pathDetails.isValid) {
                if (getUserPathFromURL === RouterPathConfig.DATA_MANAGEMENT) {
                } else {
                    this.setRouteAndMenuItem(this.sideMenuList, pathDetails.index);
                }
            } else {
                this.setRouteAndFirstMenuItemAsDefault(this.sideMenuList);
            }
        } else {
            //TODO:TEMP: don't reset if url segment is over 3
            const pathWithPutUser = urlAfterUserPath.replace('/', '');
            const secondaryPath = pathWithPutUser.split('/')[0];
            const pathDetails = this.routingHandlerService.validatePath(this.sideMenuList, secondaryPath);
            const pathIndex = pathDetails.index >= 0 ? pathDetails.index : 0; // Default to first item if not found
            this.sidebarMenuService.updateActiveItem.next(pathIndex);
            this.sidebarMenuService.storeViewDetails(this.sideMenuList[pathIndex]);
        }
    }

    handleDataManagementPanel(urlAfterUserPath) {
        const pathWithPutUser = urlAfterUserPath.replace('/', '');
        const urlSegments = pathWithPutUser.split('/');
        const secondaryPath = urlSegments[1]; //NOTE: 1 not 0

        if (secondaryPath) {
            const pathDetails = this.routingHandlerService.validatePath(this.sideMenuList, secondaryPath);
            let pathIndex = pathDetails.index >= 0 ? pathDetails.index : 0; // Default to first item if not found

            if (pathWithPutUser.split('/')[0] === 'periodic-review' && pathDetails.index == -1) {
                if (localStorage.getItem('reportReview') === 'reports') {
                    pathIndex = 0;
                } else {
                    pathIndex = 1;
                }
            }
            this.sidebarMenuService.updateActiveItem.next(pathIndex);
            this.sidebarMenuService.storeViewDetails(this.sideMenuList[pathIndex]);
        } else {
            this.sidebarMenuService.updateActiveItem.next(0);
        }
    }

    setRouteAndFirstMenuItemAsDefault(sideMenuList) {
        if (!sideMenuList || sideMenuList.length === 0) {
            return; // Don't proceed if no menu items
        }

        const activeId = this.setActiveIdForSideMenu();

        // highlight first menu item as active
        this.sidebarMenuService.updateActiveItem.next(activeId);

        //store view details
        this.sidebarMenuService.storeViewDetails(sideMenuList[activeId]);

        // update path to reflect the page we are on
        this.router.navigate([this.userPath, sideMenuList[activeId].path], {
            relativeTo: this.route
        });
    }

    setRouteAndMenuItem(sideMenuList, pathIndex) {
        if (!sideMenuList || sideMenuList.length === 0 || pathIndex < 0 || pathIndex >= sideMenuList.length) {
            return; // Don't proceed if invalid data
        }

        this.sidebarMenuService.updateActiveItem.next(pathIndex);
        this.sidebarMenuService.storeViewDetails(sideMenuList[pathIndex]);
        this.router.navigate([this.userPath, sideMenuList[pathIndex].path], {
            relativeTo: this.route
        });
    }

    updateCount(requestsCount: RequestCountResponse) {
        if (!this.sideMenuList || this.sideMenuList.length === 0) {
            return; // Don't proceed if no menu items
        }

        this.sideMenuList.forEach(element => {
            element.count = requestsCount[TuwaiqConfig.REQUESTS_COUNT_KEYS[element.viewType]];
            if (element.title == 'callRecord') {
                element.count = requestsCount.totalRequests;
            }
        });
    }

    subscribeToUpdateCount() {
        this.subs.sink = this.requestsService.updateCount.subscribe((updateCount: boolean) => {
            this.getCountList();
        });
    }

    setActiveIdForSideMenu() {
        let activeId = 0;
        if (this.userRole === USER_ROLE.ADMIN && this.sideMenuList && this.sideMenuList.length > 0) {
            const index = this.sideMenuList.findIndex(e => e.viewType === VIEW_TYPE.DASHBOARD);
            activeId = index !== -1 ? index : 0;
        }
        return activeId;
    }

    handleLoaderOverlay() {
        this.subs.sink = this.requestsService.showLoaderOverlay.subscribe((showLoaderOverlay: boolean) => {
            this.showLoaderOverlay = showLoaderOverlay;
        });
    }

    getLoggedUserId() {
        if (isPlatformBrowser(this.platformId)) {
            return this.cookies.get('userId');
        } else {
            return null;
        }
    }

    subScrollToTp() {
        this.subs.sink = this.requestsService.scrollToTop.subscribe((showLoaderOverlay: boolean) => {
            if (this.tableInput) {
                this.tableInput.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}
